/**
 * All SVG chart components. Data-only inputs; no data fetching here.
 */

// ------------------ Retention curve (M0..M5 for one cohort) ------------------
export function RetentionCurve({ cells }) {
  // cells: [{active,rate}|null, ...] length 13
  const W = 420, H = 260, padL = 36, padR = 10, padT = 16, padB = 34;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const yFor = v => padT + (1 - v / 100) * plotH;
  const xFor = i => padL + (i / 5) * plotW;
  const pts = cells.slice(0, 6);
  const line = pts.map((c, i) => c ? `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(c.rate)}` : '').filter(Boolean).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%">
      {[0, 25, 50, 75, 100].map(v => (
        <g key={v}>
          <line x1={padL} y1={yFor(v)} x2={W - padR} y2={yFor(v)} stroke="#EAD9C0" strokeWidth="0.5" />
          <text x={padL - 6} y={yFor(v) + 3} textAnchor="end" fontFamily="IBM Plex Mono" fontSize="9.5" fill="#8A7B67">{v}%</text>
        </g>
      ))}
      <path d={line} fill="none" stroke="#E9591C" strokeWidth="2.5" />
      {pts.map((c, i) => c && (
        <circle key={i} cx={xFor(i)} cy={yFor(c.rate)} r="4" fill="#E9591C" />
      ))}
      {['M0','M1','M2','M3','M4','M5'].map((l,i) => (
        <text key={l} x={xFor(i)} y={H - 10} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="11" fill="#8A7B67">{l}</text>
      ))}
    </svg>
  );
}

// ------------------ M1/M3 by channel bars ------------------
export function ChannelBars({ channels }) {
  // channels: { All: cellsArr, Retail: cellsArr, ... } - we look at M1 and M3 of the current-anchored cohort (passed as cellsAt(1)/cellsAt(3))
  // For simplicity, expect { Omni:{m1,m3}, Retail:{m1,m3}, 'E-Commerce':{m1,m3} }
  const W = 420, H = 260, padL = 36, padR = 10, padT = 20, padB = 34;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const chans = ['Omni','Retail','E-Commerce'];
  const colors = {Omni:'#E9591C',Retail:'#8A7B67','E-Commerce':'#B47512'};
  const groups = ['m1','m3']; const groupLabels = ['M1','M3'];
  const groupW = plotW/groups.length, barW = 28;
  const yFor = v => padT + (1 - v/100) * plotH;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%">
      {[0,25,50,75,100].map(v => (
        <g key={v}>
          <line x1={padL} y1={yFor(v)} x2={W-padR} y2={yFor(v)} stroke="#EAD9C0" />
          <text x={padL-6} y={yFor(v)+3} textAnchor="end" fontFamily="IBM Plex Mono" fontSize="9.5" fill="#8A7B67">{v}%</text>
        </g>
      ))}
      {groups.map((mkey, gi) => {
        const gc = padL + gi * groupW + groupW/2;
        const totalW = chans.length*barW + (chans.length-1)*4;
        let sx = gc - totalW/2;
        return (
          <g key={mkey}>
            {chans.map(ch => {
              const v = channels?.[ch]?.[mkey];
              const el = v == null
                ? <g key={ch}>
                    <rect x={sx} y={padT+plotH-3} width={barW} height={3} rx="1.5" fill="#EAD9C0" />
                    <text x={sx+barW/2} y={padT+plotH-6} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9" fill="#B8A98F">n/a</text>
                  </g>
                : (() => {
                    const y = yFor(v), h = (padT+plotH) - y;
                    return (
                      <g key={ch}>
                        <rect x={sx} y={y} width={barW} height={h} rx="3" fill={colors[ch]} />
                        <text x={sx+barW/2} y={y-5} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9.5" fill="#1F1B17" fontWeight="700">{v.toFixed(0)}%</text>
                      </g>
                    );
                  })();
              sx += barW + 4;
              return el;
            })}
            <text x={gc} y={H-10} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="11" fill="#8A7B67">{groupLabels[gi]}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ------------------ Trend line (M1 or M3 per cohort) ------------------
export function TrendLine({ trend, metricKey }) {
  // trend: [{label,size,m1_pct,m3_pct,excluded}, ...]
  const W = 900, H = 280, padL = 48, padR = 24, padT = 24, padB = 44;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const pts = trend.filter(p => !p.excluded && p[metricKey] != null);
  if (!pts.length) return <svg viewBox={`0 0 ${W} ${H}`} />;

  const values = pts.map(p => p[metricKey]);
  const vMax = Math.max(...values), vMin = Math.min(...values);
  const yMax = Math.ceil((vMax + 3) / 5) * 5;
  const yMin = Math.max(0, Math.floor((vMin - 3) / 5) * 5);
  const yFor = v => padT + (1 - (v - yMin) / (yMax - yMin)) * plotH;
  const n = pts.length;
  const xFor = i => padL + (i / (n - 1)) * plotW;

  const areaPath = `M${xFor(0)} ${padT+plotH} ` +
    pts.map((p,i) => `L${xFor(i)} ${yFor(p[metricKey])}`).join(' ') +
    ` L${xFor(n-1)} ${padT+plotH} Z`;
  const linePath = pts.map((p,i) => `${i===0?'M':'L'}${xFor(i)} ${yFor(p[metricKey])}`).join(' ');

  const gridSteps = [];
  for (let v = yMin; v <= yMax; v += 5) gridSteps.push(v);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%">
      {gridSteps.map(v => (
        <g key={v}>
          <line x1={padL} y1={yFor(v)} x2={W-padR} y2={yFor(v)} stroke="#EAD9C0" strokeWidth="0.5" />
          <text x={padL-8} y={yFor(v)+3} textAnchor="end" fontFamily="IBM Plex Mono" fontSize="10" fill="#8A7B67">{v}%</text>
        </g>
      ))}
      <path d={areaPath} fill="#E9591C" opacity="0.06" />
      <path d={linePath} fill="none" stroke="#E9591C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p,i) => (
        <g key={i}>
          <circle cx={xFor(i)} cy={yFor(p[metricKey])} r="3.5" fill="#E9591C" />
          <text x={xFor(i)} y={yFor(p[metricKey])-8} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10" fontWeight="700" fill="#1F1B17">{p[metricKey]}%</text>
        </g>
      ))}
      {pts.map((p,i) => {
        const short = p.label.slice(5);
        const yr = p.label.endsWith('-01') || i === 0 ? "'" + p.label.slice(2,4) : '';
        return (
          <g key={`x${i}`}>
            <text x={xFor(i)} y={H-24} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10" fontWeight="600" fill="#8A7B67">{short}</text>
            {yr && <text x={xFor(i)} y={H-12} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9" fill="#B8A98F">{yr}</text>}
          </g>
        );
      })}
    </svg>
  );
}

// ------------------ Bar chart with target line (Impact tab) ------------------
export function ImpactBar({ trend, metricKey, target, baseline }) {
  const W = 900, H = 300, padL = 52, padR = 130, padT = 28, padB = 54;
  const plotW = W-padL-padR, plotH = H-padT-padB;
  const cohorts = trend.filter(c => !c.excluded);
  const n = cohorts.length;
  const barW = (plotW / n) * 0.6;

  const valMax = Math.max(target, ...cohorts.map(c => c[metricKey] || 0)) * 1.25;
  const yFor = v => padT + (1 - v/valMax) * plotH;
  const xFor = i => padL + (i+0.5) * (plotW/n) - barW/2;
  const step = valMax < 15 ? 5 : (valMax < 40 ? 10 : 25);
  const gridVals = []; for (let v=0; v<=valMax; v+=step) gridVals.push(v);

  const ty = yFor(target);
  const by = baseline != null ? yFor(baseline) : null;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%">
      {gridVals.map(v => (
        <g key={v}>
          <line x1={padL} y1={yFor(v)} x2={W-padR} y2={yFor(v)} stroke="#EAD9C0" strokeWidth="0.5" />
          <text x={padL-8} y={yFor(v)+3} textAnchor="end" fontFamily="IBM Plex Mono" fontSize="10" fill="#8A7B67">{v}%</text>
        </g>
      ))}
      {by != null && (
        <g>
          <line x1={padL} y1={by} x2={W-padR} y2={by} stroke="#8A7B67" strokeWidth="1.2" strokeDasharray="4 3" />
          <text x={W-padR+6} y={by+3} fontFamily="IBM Plex Mono" fontSize="10.5" fill="#8A7B67" fontWeight="700">Baseline {baseline}%</text>
        </g>
      )}
      <line x1={padL} y1={ty} x2={W-padR} y2={ty} stroke="#3E8E5A" strokeWidth="2" strokeDasharray="8 4" />
      <text x={W-padR+6} y={ty+3} fontFamily="IBM Plex Mono" fontSize="10.5" fill="#3E8E5A" fontWeight="700">Q4 target {target}%</text>
      {cohorts.map((c, i) => {
        const v = c[metricKey];
        const cx = xFor(i) + barW/2;
        if (v == null) {
          return (
            <g key={i}>
              <rect x={xFor(i)} y={padT+plotH-4} width={barW} height={4} fill="#EAD9C0" rx="2" />
              <text x={cx} y={padT+plotH-8} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10" fill="#B8A98F">n/a</text>
              <text x={cx} y={H-28} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10.5" fontWeight="600" fill="#8A7B67">{c.label.slice(5)}</text>
            </g>
          );
        }
        const y = yFor(v), h = (padT+plotH) - y;
        const barCol = v >= target ? '#3E8E5A' : (baseline != null && v >= baseline ? '#E9591C' : '#C0392B');
        const short = c.label.slice(5), yr = c.label.endsWith('-01') || i === 0 ? "'" + c.label.slice(2,4) : '';
        return (
          <g key={i}>
            <rect x={xFor(i)} y={y} width={barW} height={h} fill={barCol} rx="3" />
            <text x={cx} y={y-7} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="11" fontWeight="700" fill="#1F1B17">{v}%</text>
            <text x={cx} y={H-28} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10.5" fontWeight="600" fill="#8A7B67">{short}</text>
            {yr && <text x={cx} y={H-16} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="9.5" fill="#B8A98F">{yr}</text>}
          </g>
        );
      })}
    </svg>
  );
}
